"use client";

import { useState } from "react";
import { Icon } from "@/components/ui/icon";
import {
  useSeatInfo,
  useSeatAssignments,
  useSubscription,
  useActivateSeat,
  useDeactivateSeat,
  useAddSeat,
  useRemoveSeat,
} from "../hooks/use-subscription";
import type { SeatAssignment } from "../hooks/use-subscription";

export function SeatManagement() {
  const { data: seatInfo, isLoading: seatsLoading } = useSeatInfo();
  const { data: assignments, isLoading: assignmentsLoading } =
    useSeatAssignments();
  const { data: subscription } = useSubscription();

  const activateMutation = useActivateSeat();
  const deactivateMutation = useDeactivateSeat();
  const addSeatMutation = useAddSeat();
  const removeSeatMutation = useRemoveSeat();

  const [confirmDeactivate, setConfirmDeactivate] = useState<string | null>(
    null,
  );

  if (seatsLoading || assignmentsLoading) {
    return <SeatManagementSkeleton />;
  }

  if (!seatInfo) return null;

  const { seat_count, active_seats, available_seats } = seatInfo;
  const usedPercent = seat_count > 0 ? (active_seats / seat_count) * 100 : 0;
  const canRemoveSeat = available_seats > 0;
  const maxSeats = subscription?.plan.max_seats ?? null;
  const atMaxSeats = maxSeats !== null && seat_count >= maxSeats;
  const isSoloPlan = maxSeats === 1;

  return (
    <div className="rounded-lg border border-white/10 bg-white/2">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex flex-col gap-1.5">
          <h3 className="text-sm font-semibold text-heading">Team Seats</h3>
          <span className="text-sm text-muted-foreground">
            {active_seats} of {seat_count} seats used
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => addSeatMutation.mutate(1)}
            disabled={addSeatMutation.isPending || atMaxSeats}
            className="h-8 px-3 rounded-md text-sm font-medium bg-[#2b5df3] hover:bg-[#2451d6] text-white transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {addSeatMutation.isPending ? (
              <Icon
                name="IconLoadingCircle"
                size={14}
                className="animate-spin"
              />
            ) : (
              <>
                <Icon name="IconPlusSmall" size={14} />
                Add Seat
              </>
            )}
          </button>
          <div className="relative group">
            <button
              type="button"
              onClick={() =>
                canRemoveSeat && !isSoloPlan && removeSeatMutation.mutate(1)
              }
              disabled={
                !canRemoveSeat || removeSeatMutation.isPending || isSoloPlan
              }
              className="h-8 px-3 rounded-md text-sm font-medium bg-white/4 border border-white/15 text-white/70 hover:text-white hover:bg-white/8 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05),inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]"
            >
              {removeSeatMutation.isPending ? (
                <Icon
                  name="IconLoadingCircle"
                  size={14}
                  className="animate-spin"
                />
              ) : (
                <>
                  <Icon name="IconMinusSmall" size={14} />
                  Remove
                </>
              )}
            </button>
            {!canRemoveSeat && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5 rounded-md bg-[#1f1f21] border border-white/10 text-xs text-white/70 whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-10">
                Deactivate a seat first
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-5 pb-4">
        <div className="h-1.5 w-full rounded-full bg-white/8">
          <div
            className="bg-[#2b5df3] h-full rounded-full transition-all duration-500"
            style={{
              width: `${Math.min(usedPercent, 100)}%`,
              minWidth: usedPercent > 0 ? "6px" : undefined,
            }}
          />
        </div>
      </div>

      {/* Member list */}
      {assignments && assignments.length > 0 && (
        <div className="border-t border-white/10">
          <div className="px-5 py-2.5 grid grid-cols-[1fr_auto_auto_auto] gap-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            <span>Member</span>
            <span className="w-16 text-center">Role</span>
            <span className="w-16 text-center">Status</span>
            <span className="w-24 text-right">Action</span>
          </div>
          <div className="divide-y divide-white/6">
            {assignments.map((a) => (
              <MemberRow
                key={a.id}
                assignment={a}
                isConfirmingDeactivate={confirmDeactivate === a.user_id}
                onConfirmDeactivate={() => setConfirmDeactivate(a.user_id)}
                onCancelDeactivate={() => setConfirmDeactivate(null)}
                onActivate={() => activateMutation.mutate(a.user_id)}
                onDeactivate={() => {
                  deactivateMutation.mutate(a.user_id);
                  setConfirmDeactivate(null);
                }}
                activating={
                  activateMutation.isPending &&
                  activateMutation.variables === a.user_id
                }
                deactivating={
                  deactivateMutation.isPending &&
                  deactivateMutation.variables === a.user_id
                }
              />
            ))}
          </div>
        </div>
      )}

      {assignments && assignments.length === 0 && (
        <div className="border-t border-white/10 px-5 py-8 text-center text-sm text-muted-foreground">
          No seat assignments yet
        </div>
      )}
    </div>
  );
}

interface MemberRowProps {
  assignment: SeatAssignment;
  isConfirmingDeactivate: boolean;
  onConfirmDeactivate: () => void;
  onCancelDeactivate: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  activating: boolean;
  deactivating: boolean;
}

function MemberRow({
  assignment,
  isConfirmingDeactivate,
  onConfirmDeactivate,
  onCancelDeactivate,
  onActivate,
  onDeactivate,
  activating,
  deactivating,
}: MemberRowProps) {
  const isActive = assignment.status === "active";
  const displayName =
    assignment.user_name || assignment.user_email || "Unknown";

  return (
    <div className="px-5 py-3 grid grid-cols-[1fr_auto_auto_auto] gap-4 items-center">
      <div className="flex items-center gap-3 min-w-0">
        <div className="size-8 rounded-full bg-white/6 flex items-center justify-center shrink-0">
          <Icon name="IconUser" size={14} className="text-muted-foreground" />
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-medium text-heading truncate">
            {displayName}
          </span>
          {assignment.user_name && assignment.user_email && (
            <span className="text-xs text-muted-foreground truncate">
              {assignment.user_email}
            </span>
          )}
        </div>
      </div>

      <span className="w-16 text-center text-xs text-muted-foreground capitalize">
        {assignment.role ?? "member"}
      </span>

      <span className="w-16 flex justify-center">
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
            isActive
              ? "bg-emerald-500/10 text-emerald-400"
              : "bg-white/4 text-muted-foreground"
          }`}
        >
          <span
            className={`size-1.5 rounded-full ${isActive ? "bg-emerald-400" : "bg-white/30"}`}
          />
          {isActive ? "Active" : "Inactive"}
        </span>
      </span>

      <div className="w-24 flex justify-end">
        {isConfirmingDeactivate ? (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={onDeactivate}
              disabled={deactivating}
              className="h-7 px-2 rounded text-xs font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors cursor-pointer disabled:opacity-40 flex items-center gap-1"
            >
              {deactivating ? (
                <Icon
                  name="IconLoadingCircle"
                  size={12}
                  className="animate-spin"
                />
              ) : (
                "Confirm"
              )}
            </button>
            <button
              type="button"
              onClick={onCancelDeactivate}
              className="h-7 px-2 rounded text-xs font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              Cancel
            </button>
          </div>
        ) : isActive ? (
          <button
            type="button"
            onClick={onConfirmDeactivate}
            className="h-7 px-2.5 rounded text-xs font-medium bg-white/4 border border-white/15 text-white/70 hover:text-white hover:bg-white/8 transition-colors cursor-pointer shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05),inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]"
          >
            Deactivate
          </button>
        ) : (
          <button
            type="button"
            onClick={onActivate}
            disabled={activating}
            className="h-7 px-2.5 rounded text-xs font-medium bg-[#2b5df3] hover:bg-[#2451d6] text-white transition-colors cursor-pointer disabled:opacity-40 flex items-center gap-1"
          >
            {activating ? (
              <Icon
                name="IconLoadingCircle"
                size={12}
                className="animate-spin"
              />
            ) : (
              "Activate"
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function SeatManagementSkeleton() {
  return (
    <div className="rounded-lg border border-white/10 bg-white/2">
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex flex-col gap-1.5">
          <div className="h-4 w-24 rounded bg-white/6 animate-pulse" />
          <div className="h-3.5 w-32 rounded bg-white/4 animate-pulse" />
        </div>
        <div className="flex items-center gap-2">
          <div className="h-8 w-24 rounded-md bg-white/6 animate-pulse" />
          <div className="h-8 w-20 rounded-md bg-white/4 animate-pulse" />
        </div>
      </div>
      <div className="px-5 pb-4">
        <div className="h-1.5 w-full rounded-full bg-white/8 animate-pulse" />
      </div>
      <div className="border-t border-white/10">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="px-5 py-3 flex items-center gap-4 border-b border-white/6 last:border-0"
          >
            <div className="size-8 rounded-full bg-white/6 animate-pulse" />
            <div className="flex-1 flex flex-col gap-1">
              <div className="h-3.5 w-28 rounded bg-white/6 animate-pulse" />
              <div className="h-3 w-40 rounded bg-white/4 animate-pulse" />
            </div>
            <div className="h-5 w-14 rounded-full bg-white/4 animate-pulse" />
            <div className="h-7 w-20 rounded bg-white/4 animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  );
}
